import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './css/RecipesList.css';
import filterIcon from './assets/filter.webp';
import personImage from './assets/person.png';
import profile from './assets/profile.png';
import { motion, AnimatePresence } from 'framer-motion';
import image1 from './assets/image1.png';
import image2 from './assets/image2.png';
import image3 from './assets/image3.png';
import image4 from './assets/image4.png';
import bigImg from './assets/bigImg.jpg';
import img1 from './assets/1chef.webp';
import img2 from './assets/2chef.webp';
import img3 from './assets/3chef.webp';
import img4 from './assets/4chef.webp';
import SEO from './components/SEO';

function RecipesList({ searchQuery, setSearchQuery }) {
  const navigate = useNavigate();
  const [recipes, setRecipes] = useState([]);
  const [filteredRecipes, setFilteredRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [showFilter, setShowFilter] = useState(false);
  const [isVegetarian, setIsVegetarian] = useState(false);
  const [minCalories, setMinCalories] = useState(0);   // Min calories
  const [maxCalories, setMaxCalories] = useState(1000); // Max calories
  const [minProteins, setMinProteins] = useState(0);   // Min proteins
  const [maxProteins, setMaxProteins] = useState(100); // Max proteins
  const [isFiltering, setIsFiltering] = useState(false); // Added for loading animation
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // New recipe category states
  const [chickenRecipes, setChickenRecipes] = useState([]);
  const [porkRecipes, setPorkRecipes] = useState([]);
  const [highProteinRecipes, setHighProteinRecipes] = useState([]);
  const [loadingSections, setLoadingSections] = useState(true);

  const filterRef = useRef(null);  // Reference for the filter menu
  const observer = useRef();
  const loadingRef = useRef(null);
  const trendingRef = useRef(null);  // Add reference for Trending Recipes section

  const RECIPES_PER_PAGE = 12;
  const RECIPES_PER_SECTION = 3;
  
  // Track window width for responsive elements
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Define mobile breakpoint
  const isMobile = windowWidth <= 768;

  // Setup Intersection Observer for infinite scrolling
  const lastRecipeElementRef = useCallback(node => {
    if (loading || loadingMore) return;
    if (observer.current) observer.current.disconnect();
    
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        loadMoreRecipes();
      }
    }, { threshold: 0.5 });
    
    if (node) observer.current.observe(node);
  }, [loading, loadingMore, hasMore]);

  // Initial recipe fetch
  useEffect(() => {
    fetchRecipes(1, true);
    fetchCategorizedRecipes();
  }, []);

  // Function to fetch all categorized recipes
  const fetchCategorizedRecipes = async () => {
    setLoadingSections(true);
    
    try {
      await Promise.all([
        fetchChickenRecipes(),
        fetchPorkRecipes(),
        fetchHighProteinRecipes()
      ]);
    } catch (error) {
      console.error('Error fetching categorized recipes:', error);
    } finally {
      setLoadingSections(false);
    }
  };

  // Function to fetch chicken recipes
  const fetchChickenRecipes = async () => {
    try {
      const response = await fetch(`https://starfish-app-9uavi.ondigitalocean.app/recipes/search?q=chicken&limit=${RECIPES_PER_SECTION}&visibility=true`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data && Array.isArray(data)) {
        setChickenRecipes(data);
      } else if (data && data.recipes && Array.isArray(data.recipes)) {
        setChickenRecipes(data.recipes);
      } else {
        console.error('Invalid data format for chicken recipes:', data);
        setChickenRecipes([]);
      }
    } catch (error) {
      console.error('Error fetching chicken recipes:', error);
      setChickenRecipes([]);
    }
  };

  // Function to fetch pork recipes
  const fetchPorkRecipes = async () => {
    try {
      const response = await fetch(`https://starfish-app-9uavi.ondigitalocean.app/recipes/search?q=pork&limit=${RECIPES_PER_SECTION}&visibility=true`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data && Array.isArray(data)) {
        setPorkRecipes(data);
      } else if (data && data.recipes && Array.isArray(data.recipes)) {
        setPorkRecipes(data.recipes);
      } else {
        console.error('Invalid data format for pork recipes:', data);
        setPorkRecipes([]);
      }
    } catch (error) {
      console.error('Error fetching pork recipes:', error);
      setPorkRecipes([]);
    }
  };

  // Function to fetch high protein recipes
  const fetchHighProteinRecipes = async () => {
    try {
      // Assuming the API supports this kind of filtering. If not, you may need to
      // fetch all recipes and filter locally or implement a custom endpoint
      const response = await fetch(`https://starfish-app-9uavi.ondigitalocean.app/recipes?minProteins=20&limit=${RECIPES_PER_SECTION}&visibility=true`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data && Array.isArray(data)) {
        setHighProteinRecipes(data);
      } else if (data && data.recipes && Array.isArray(data.recipes)) {
        setHighProteinRecipes(data.recipes);
      } else {
        console.error('Invalid data format for high protein recipes:', data);
        setHighProteinRecipes([]);
      }
    } catch (error) {
      console.error('Error fetching high protein recipes:', error);
      setHighProteinRecipes([]);
    }
  };

  // Apply filters when filter criteria change
  useEffect(() => {
    if (searchQuery && searchQuery.trim() !== '') {
      fetchSearchResults(searchQuery);
      // Hide category sections when searching
      setLoadingSections(false);
    } else if (recipes.length > 0) {
      resetAndApplyFilters();
      // Show category sections when not searching
      fetchCategorizedRecipes();
    }
  }, [searchQuery]);

  // Apply non-search filters when filter criteria change
  useEffect(() => {
    if (recipes.length > 0 && !searchQuery) {
      resetAndApplyFilters();
    }
  }, [isVegetarian, minCalories, maxCalories, minProteins, maxProteins]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close filter menu if click is outside the filter menu
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setShowFilter(false);
      }
    };

    // Add event listener when the filter is shown
    if (showFilter) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      // Cleanup the event listener when the component is unmounted
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFilter]);

  // Toggle the filter menu
  const toggleFilterMenu = () => {
    setShowFilter(!showFilter);
  };

  // Function to fetch recipes with pagination
  const fetchRecipes = async (page, resetList = false) => {
    try {
      resetList ? setLoading(true) : setLoadingMore(true);
      
      const response = await fetch(`https://starfish-app-9uavi.ondigitalocean.app/recipes?page=${page}&limit=${RECIPES_PER_PAGE}&visibility=true`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log(`Fetched recipes for page ${page}:`, data);
      
      let newRecipes = [];
      
      // Check if data is an object with a recipes property
      if (data && data.recipes && Array.isArray(data.recipes)) {
        newRecipes = data.recipes;
      }
      // Check if data itself is an array
      else if (Array.isArray(data)) {
        newRecipes = data;
      } else {
        console.error('Received invalid data format:', data);
        newRecipes = [];
      }
      
      // Check if we've reached the end of available recipes
      if (newRecipes.length < RECIPES_PER_PAGE) {
        setHasMore(false);
      }
      
      // Update the recipes state
      if (resetList) {
        setRecipes(newRecipes);
        setFilteredRecipes(newRecipes);
      } else {
        setRecipes(prevRecipes => [...prevRecipes, ...newRecipes]);
        
        // Only append filtered recipes if we're not actively filtering
        if (!isFiltering) {
          setFilteredRecipes(prevFiltered => [...prevFiltered, ...newRecipes]);
        } else {
          // If we are filtering, apply filters to the new combined set
          applyFilters([...recipes, ...newRecipes]);
        }
      }
    } catch (error) {
      console.error('Error fetching recipes:', error);
      setHasMore(false);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Function to load more recipes when scrolling
  const loadMoreRecipes = () => {
    if (!hasMore || loading || loadingMore) return;
    
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    fetchRecipes(nextPage);
  };

  // Reset pagination and apply filters
  const resetAndApplyFilters = () => {
    setCurrentPage(1);
    setHasMore(true);
    applyFilters(recipes);
  };

  // Function to apply all filters
  const applyFilters = (recipesToFilter = recipes) => {
    setIsFiltering(true);
    
    if (!Array.isArray(recipesToFilter)) {
      console.error('recipes is not an array:', recipesToFilter);
      setIsFiltering(false);
      return;
    }
    
    console.log('Applying filters to', recipesToFilter.length, 'recipes');
    console.log('Current filters:', { isVegetarian, minCalories, maxCalories, minProteins, maxProteins, searchQuery });
    
    let filtered = [...recipesToFilter];

    // Filter by vegetarian option
    if (isVegetarian) {
      filtered = filtered.filter(recipe => 
        recipe.ingredients?.some(ingredient => 
          ingredient?.toLowerCase().includes('vegetarian')
        )
      );
      console.log('After vegetarian filter:', filtered.length, 'recipes');
    }

    // Filter by calorie range
    filtered = filtered.filter(recipe =>
      recipe.calories >= minCalories && recipe.calories <= maxCalories
    );
    console.log('After calorie filter:', filtered.length, 'recipes');

    // Filter by protein range
    filtered = filtered.filter(recipe =>
      recipe.proteins >= minProteins && recipe.proteins <= maxProteins
    );
    console.log('After protein filter:', filtered.length, 'recipes');

    // Update filtered recipes
    setFilteredRecipes(filtered);
    console.log('Final filtered recipes:', filtered.length);
    
    setTimeout(() => {
      setIsFiltering(false);
    }, 300);
  };

  // Filter recipes function called by Apply Filters button
  const filterRecipes = () => {
    resetAndApplyFilters();
    setShowFilter(false); // Close filter menu after applying
  };

  // Reset all filters
  const resetFilters = () => {
    setSearchQuery('');
    setIsVegetarian(false);
    setMinCalories(0);
    setMaxCalories(1000);
    setMinProteins(0);
    setMaxProteins(100);
    
    // Reload initial recipes if we were in a search
    fetchRecipes(1, true);
    
    // Reload category sections
    fetchCategorizedRecipes();
    
    setCurrentPage(1);
    setHasMore(true);
    setShowFilter(false); // Close filter menu after resetting
    console.log('Filters reset, showing all recipes');
  };

  // Function to fetch search results
  const fetchSearchResults = async (query) => {
    try {
      setLoading(true);
      setIsFiltering(true);
      
      const response = await fetch(`https://starfish-app-9uavi.ondigitalocean.app/recipes/search?q=${encodeURIComponent(query)}&visibility=true`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log(`Fetched search results for "${query}":`, data);
      
      let searchResults = [];
      
      // Check if data is an object with a recipes property
      if (data && data.recipes && Array.isArray(data.recipes)) {
        searchResults = data.recipes;
      }
      // Check if data itself is an array
      else if (Array.isArray(data)) {
        searchResults = data;
      } else {
        console.error('Received invalid data format:', data);
        searchResults = [];
      }
      
      // Update the recipes and filtered recipes states
      setFilteredRecipes(searchResults);
      
      // Also update the full recipes list if we got search results
      if (searchResults.length > 0) {
        setRecipes(searchResults);
      }
      
      // Reset pagination since we have a new result set
      setCurrentPage(1);
      setHasMore(false); // Assuming search results are not paginated
      
      // Hide category sections when searching
      setLoadingSections(false);
      
    } catch (error) {
      console.error('Error fetching search results:', error);
      setFilteredRecipes([]);
      setHasMore(false);
    } finally {
      setLoading(false);
      setIsFiltering(false);
    }
  };

  // Function to handle navigation to upload page
  const handleUploadNavigation = () => {
    navigate('/recipe-add');
  };

  // Render a recipe section with a title and given recipes
  const renderRecipeSection = (title, recipes, imageIndex) => {
    if (!recipes || recipes.length === 0) return null;
    
    // Select the appropriate cooking image based on index
    const cookingImages = [image1, image2, image3, image4];
    const bannerImage = cookingImages[imageIndex % cookingImages.length];
    
    // Extract search term from title (e.g., "Chicken Recipes" -> "chicken")
    const searchTerm = title.toLowerCase().split(' ')[0];
    
    return (
      <div className="TrendingFilter">
        <div className="inner-container">
          <div className="header2">
            <h2 className="titleRecipes">{title}</h2>
          </div>
        </div>
        
        <motion.div 
          className="recipes-grid section-grid"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.25 }}
        >
          {recipes.map((recipe, index) => (
            <motion.div 
              key={recipe.id} 
              className="recipe-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.2,
                delay: Math.min(index * 0.05, 0.5),
                ease: "easeOut"
              }}
              whileHover={{ 
                y: -5,
                scale: 1.03,
                boxShadow: "0 12px 20px rgba(0,0,0,0.08)"
              }}
              whileTap={{ scale: 0.98 }}
            >
              {index === 0 && <div className="recipe-badge">Featured</div>}
              <Link to={`/recipe-details/${recipe.id}`}>
                <motion.div 
                  className="recipe-image-wrapper"
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                >
                  <motion.img 
                    src={recipe.url || 'https://via.placeholder.com/300x200?text=No+Image'} 
                    alt={`${recipe.name} - Delicious recipe with ${recipe.calories || 'N/A'} calories and ${recipe.proteins || 'N/A'}g protein`}
                    className="recipe-image"
                    loading="lazy"
                    initial={{ scale: 1.0 }}
                    whileHover={{ scale: 1.02 }}
                    transition={{ duration: 0.2, ease: "easeOut" }}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://via.placeholder.com/300x200?text=Image+Error';
                    }}
                  />
                </motion.div>
                <motion.div 
                  className="recipe-content"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.15, delay: 0.1 }}
                >
                  <motion.h2
                    whileHover={{ color: "#FF6B6B" }}
                    transition={{ duration: 0.15 }}
                  >
                    {recipe.name}
                  </motion.h2>
                  
                  <motion.div
                    className="recipe-stats"
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.15, delay: 0.15 }}
                  >
                    <p className='calories'>
                      <strong>Calories:</strong> {recipe.calories || 'N/A'}
                    </p>
                    <p className='proteins'>
                      <strong>Proteins:</strong> {recipe.proteins || 'N/A'} g
                    </p>
                  </motion.div>
                </motion.div>
              </Link>
            </motion.div>
          ))}
        </motion.div>
      </div>
    );
  };

  // Create a skeleton card component for reuse
  const SkeletonRecipeCard = () => (
    <div className="recipe-card skeleton-card">
      <div className="skeleton-image"></div>
      <div className="skeleton-content">
        <div className="skeleton-title"></div>
        <div className="skeleton-stats">
          <div className="skeleton-stat"></div>
          <div className="skeleton-stat"></div>
        </div>
      </div>
    </div>
  );

  // Render skeleton section with title and cards
  const renderSkeletonSection = (title) => (
    <div className="TrendingFilter">
      <div className="inner-container">
        <div className="header2">
          <h2 className="titleRecipes">{title}</h2>
          <div className="skeleton-view-all"></div>
        </div>
      </div>
      
      <div className="recipes-grid section-grid">
        {[...Array(3)].map((_, index) => (
          <SkeletonRecipeCard key={`skeleton-section-${title}-${index}`} />
        ))}
      </div>
    </div>
  );

  // Render skeleton grid for all recipes
  const renderSkeletonGrid = () => (
    <div className="recipes-grid">
      {[...Array(12)].map((_, index) => (
        <SkeletonRecipeCard key={`skeleton-grid-${index}`} />
      ))}
    </div>
  );

  // Add useEffect to load the Lottie script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = "https://unpkg.com/@dotlottie/player-component@2.7.12/dist/dotlottie-player.mjs";
    script.type = "module";
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  // Scroll to section handler
  const scrollToTrending = () => {
    trendingRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Generate dynamic SEO data based on current state
  const generateSEOData = () => {
    if (searchQuery && searchQuery.trim() !== '') {
      return {
        title: `${searchQuery} Recipes - Savory`,
        description: `Discover delicious ${searchQuery} recipes on Savory. Find nutritionally balanced recipes with detailed calorie and protein information.`,
        keywords: `${searchQuery} recipes, ${searchQuery} cooking, healthy ${searchQuery}, nutrition, meal planning`,
        url: `/?q=${encodeURIComponent(searchQuery)}`,
        breadcrumb: [
          { name: "Home", url: "/" },
          { name: `${searchQuery} Recipes`, url: `/?q=${encodeURIComponent(searchQuery)}` }
        ]
      };
    }
    
    return {
      title: "Savory - Discover Amazing Recipe Collections",
      description: "Explore our curated selection of delicious recipes from around the world. Find chicken recipes, pork recipes, high-protein meals, and more with detailed nutritional information.",
      keywords: "recipes, cooking, chicken recipes, pork recipes, high protein, healthy eating, meal planning, nutrition, dietary restrictions",
      url: "/",
      breadcrumb: [
        { name: "Home", url: "/" }
      ]
    };
  };

  const seoData = generateSEOData();

  return (
    <div className="recipes-list-container">
      <SEO 
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        url={seoData.url}
        breadcrumb={seoData.breadcrumb}
        type="website"
      />
      {!searchQuery && (
        <>
          {/* Large Inspirational Banner */}
          <motion.div 
            className="inspiration-banner"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7 }}
          >
            <div className="inspiration-content">                

              <div className="banner-text-section">
            
                <div className="bannzer-tag">FEATURED COLLECTIONS</div>
                <h1>Discover Amazing<br/>Recipe Collections</h1>
                <p>Explore our curated selection of the finest recipes from around the world</p>
                <motion.button 
                  className="inspiration-button"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={scrollToTrending}
                >
                  Explore Collection
                </motion.button>
              </div>
              <div className="image-grid2">
                <div className="image-item">
                  <img src={img1} alt="Professional chef preparing fresh ingredients for healthy cooking" />
                </div>
                <div className="image-item">
                  <img src={img2} alt="Chef cooking comfort food in modern kitchen" />
                </div>
                <div className="image-item">
                  <img src={img3} alt="International cuisine preparation by expert chef" />
                </div>
                <div className="image-item">
                  <img src={img4} alt="Quick and easy meal preparation techniques" />
                </div>
              </div>
            </div>
          </motion.div>

          {loadingSections ? (
            <div className="recipe-sections-container">
              
              {renderSkeletonSection("Trending Recipes")}
              {renderSkeletonSection("Chicken Recipes")}
              {renderSkeletonSection("Pork Recipes")}
            </div>
          ) : (
            <div>
            <div className="recipe-sections-container" ref={trendingRef}>
            <div className="hole-overlay2"></div>
            <div className="hole-overlay"></div>

              {renderRecipeSection("Trending Recipes", highProteinRecipes, 0)}
              </div>
           
              <div className="recipe-sections-container">
              {renderRecipeSection("Chicken Recipes", chickenRecipes, 1)}
              {renderRecipeSection("Pork Recipes", porkRecipes, 2)}
            </div>
            </div>
          )}
          
          {/* Enhanced Recipe Upload Banner with cooking image */}
        

          {/* Advanced Search Banner with image2 */}
          
        </>
      )}

      <div className="TrendingFilter">
        <div className="inner-container">
          <div className="header2">
            <h1 className="titleRecipes">All Recipes</h1>
            <div style={{ position: 'relative' }}>
              {(searchQuery || isVegetarian || minCalories > 0 || maxCalories < 1000 || minProteins > 0 || maxProteins < 100) && (
                <button 
                  className="back-home-button"
                  onClick={resetFilters}
                >
                  remove filters
                </button>
              )}
              
              {showFilter && (
                <div className="filter-menu" ref={filterRef}>
                  <div className="filter-option">
                    <label>
                      <input
                        type="checkbox"
                        checked={isVegetarian}
                        onChange={(e) => setIsVegetarian(e.target.checked)}
                      />
                      Vegetarian
                    </label>
                  </div>

                  <div className="filter-option">
                    <label>Calories Range</label>
                    <div className="calorie-range-container">
                      <input
                        type="range"
                        min="0"
                        max="1000"
                        value={minCalories}
                        onChange={(e) => setMinCalories(Number(e.target.value))}
                      />
                      <input
                        type="range"
                        min="0"
                        max="1000"
                        value={maxCalories}
                        onChange={(e) => setMaxCalories(Number(e.target.value))}
                      />
                      <div className="calorie-labels">
                        <span>{minCalories} cal</span>
                        <span>{maxCalories} cal</span>
                      </div>
                    </div>
                  </div>

                  <div className="filter-option">
                    <label>Proteins Range</label>
                    <div className="protein-range-container">
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={minProteins}
                        onChange={(e) => setMinProteins(Number(e.target.value))}
                      />
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={maxProteins}
                        onChange={(e) => setMaxProteins(Number(e.target.value))}
                      />
                      <div className="protein-labels">
                        <span>{minProteins}g</span>
                        <span>{maxProteins}g</span>
                      </div>
                    </div>
                  </div>

                  <button onClick={filterRecipes}>Apply Filters</button>
                  <button onClick={resetFilters} className="reset-button">Reset Filters</button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        renderSkeletonGrid()
      ) : (
        <>
          {filteredRecipes.length === 0 ? (
        <div className="no-recipes-found">
          <h2>No recipes found</h2>
          <p>Try adjusting your filters or search query</p>
          <button onClick={resetFilters} className="reset-button">Reset All Filters</button>
        </div>
      ) : (
        <motion.div 
          className="recipes-grid"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
              transition={{ duration: 0.25 }}
        >
          {filteredRecipes.map((recipe, index) => (
            <motion.div 
              key={recipe.id} 
              className="recipe-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                    duration: 0.2,
                    delay: Math.min(index * 0.05, 0.5),
                ease: "easeOut"
              }}
              whileHover={{ 
                    y: -5,
                    scale: 1.03,
                    boxShadow: "0 12px 20px rgba(0,0,0,0.08)"
              }}
              whileTap={{ scale: 0.98 }}
              // Attach the ref to the last element for infinite scrolling detection
              ref={index === filteredRecipes.length - 1 ? lastRecipeElementRef : null}
            >
              <Link to={`/recipe-details/${recipe.id}`}>
                <motion.div 
                  className="recipe-image-wrapper"
                      whileHover={{ scale: 1.02 }}
                      transition={{ duration: 0.2, ease: "easeOut" }}
                >
                                    <motion.img 
                    src={recipe.url || 'https://via.placeholder.com/300x200?text=No+Image'} 
                    alt={`${recipe.name} - Featured recipe with detailed nutritional information and cooking instructions`}
                    className="recipe-image"
                    loading="lazy"
                    initial={{ scale: 1.0 }}
                    whileHover={{ scale: 1.02 }}
                    transition={{ duration: 0.2, ease: "easeOut" }}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://via.placeholder.com/300x200?text=Image+Error';
                    }}
                  />
                </motion.div>
                <motion.div 
                  className="recipe-content"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                      transition={{ duration: 0.15, delay: 0.1 }}
                >
                  <motion.h2
                    whileHover={{ color: "#FF6B6B" }}
                        transition={{ duration: 0.15 }}
                  >
                    {recipe.name}
                  </motion.h2>
                  
                  <motion.div
                    className="recipe-stats"
                        initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.15, delay: 0.15 }}
                  >
                    <p className='calories'>
                      <strong>Calories:</strong> {recipe.calories || 'N/A'}
                    </p>
                    <p className='proteins'>
                      <strong>Proteins:</strong> {recipe.proteins || 'N/A'} g
                    </p>
                  </motion.div>
                </motion.div>
              </Link>
            </motion.div>
          ))}
        </motion.div>
          )}
        </>
      )}

      {!loading && filteredRecipes.length > 0 && (
        <div className="loading-more" ref={loadingRef}>
          {loadingMore && (
            <div className="loading-spinner-small"></div>
          )}
          {!hasMore && filteredRecipes.length > RECIPES_PER_PAGE && (
            <p className="no-more-recipes">No more recipes to load</p>
          )}
        </div>
      )}
    </div>
  );
}

export default RecipesList;
